import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ArtistStatusEnum, ArtistTypeEnum } from '@/lib/api-admin/types';

export type ArtistSort =
  | 'stageName,asc'
  | 'stageName,desc'
  | 'createdAt,desc'
  | 'createdAt,asc'
  | 'updatedAt,desc'
  | 'productCount,desc';

export const ARTIST_SORTABLE_COLUMNS = new Set([
  'stageName',
  'createdAt',
  'updatedAt',
  'productCount',
]);

export interface ArtistFiltersState {
  keyword: string;
  type: ArtistTypeEnum | null;
  status: ReadonlyArray<ArtistStatusEnum>;
  vendorId: string | null;
  hasNoImage: boolean;
  page: number;
  size: number;
  sort: ArtistSort;
  setKeyword: (v: string) => void;
  setType: (v: ArtistTypeEnum | null) => void;
  setStatuses: (v: ReadonlyArray<ArtistStatusEnum>) => void;
  toggleStatus: (s: ArtistStatusEnum) => void;
  setVendorId: (v: string | null) => void;
  setHasNoImage: (v: boolean) => void;
  setPage: (v: number) => void;
  setSize: (v: number) => void;
  setSort: (v: ArtistSort) => void;
  reset: () => void;
}

const INITIAL = {
  keyword: '',
  type: null as ArtistTypeEnum | null,
  status: [] as ReadonlyArray<ArtistStatusEnum>,
  vendorId: null as string | null,
  hasNoImage: false,
  page: 1,
  size: 20,
  sort: 'createdAt,desc' as ArtistSort,
};

export const useArtistFiltersStore = create<ArtistFiltersState>()(
  devtools(
    (set) => ({
      ...INITIAL,
      setKeyword: (keyword) => set({ keyword, page: 1 }),
      setType: (type) => set({ type, page: 1 }),
      setStatuses: (status) => set({ status, page: 1 }),
      toggleStatus: (s) =>
        set((state) => {
          const next = state.status.includes(s)
            ? state.status.filter((x) => x !== s)
            : [...state.status, s];
          return { status: next, page: 1 };
        }),
      setVendorId: (vendorId) => set({ vendorId, page: 1 }),
      setHasNoImage: (hasNoImage) => set({ hasNoImage, page: 1 }),
      setPage: (page) => set({ page }),
      setSize: (size) => set({ size, page: 1 }),
      setSort: (sort) => set({ sort, page: 1 }),
      reset: () => set({ ...INITIAL }),
    }),
    { name: 'artist-filters' },
  ),
);
